import {
  BaseListResponseDto,
  DateRangeQueryParamsDto,
  PaginationQueryParams,
  SortDirectionQueryParams,
} from './common.dtos';
import { OfferExternalCheckInstructionsDto, OfferExternalWireInstructionsDto } from './offers.dtos';

export enum OfferOrderPaymentTypeDto {
  Ach = 'Ach',
  Wire = 'Wire',
  Check = 'Check',
  Card = 'Card',
  Acat = 'Acat',
}

export enum OfferOrderRetirementAccountFundingTypeDto {
  IraContribution = 'IraContribution',
  DirectFromIdenticalIra = 'DirectFromIdenticalIra',
}

export enum OfferOrderStatusDto {
  PendingFunds = 'PendingFunds',
  PendingAction = 'PendingAction',
  PendingOfferClose = 'PendingOfferClose',
  Approved = 'Approved',
  Complete = 'Complete',
  PendingFirmCancellation = 'PendingFirmCancellation',
  Cancelled = 'Cancelled',
  Rejected = 'Rejected',
  Deleted = 'Deleted',
}

export enum OfferOrderCardPaymentIntentStatusDto {
  RequiresPaymentMethod = 'RequiresPaymentMethod',
  RequiresConfirmation = 'RequiresConfirmation',
  RequiresAction = 'RequiresAction',
  Processing = 'Processing',
  RequiresCapture = 'RequiresCapture',
  Canceled = 'Canceled',
  Succeeded = 'Succeeded',
}

export enum OfferOrderRefundStatusDto {
  Pending = 'Pending',
  Failed = 'Failed',
  Succeeded = 'Succeeded',
  Canceled = 'Canceled',
  RequiresAction = 'RequiresAction',
}

export enum OfferOrderRefundReasonDto {
  Duplicate = 'Duplicate',
  Fraudulent = 'Fraudulent',
  RequestedByCustomer = 'RequestedByCustomer',
}

export enum OfferOrderRefundFailureReasonDto {
  LostOrStolenCard = 'LostOrStolenCard',
  ExpiredOrCanceledCard = 'ExpiredOrCanceledCard',
  ChargeForPendingRefundDisputed = 'ChargeForPendingRefundDisputed',
  InsufficientFunds = 'InsufficientFunds',
  Declined = 'Declined',
  MerchantRequest = 'MerchantRequest',
  Unknown = 'Unknown',
}

export interface OfferOrderCardPaymentDto {
  intentId: string;
  intentStatus: OfferOrderCardPaymentIntentStatusDto;
  isConfirmed: boolean;
  amountRefunded: number;
  cardholder?: string;
}

export enum OfferOrderRefundMechanismDto {
  Ach = 'Ach',
  Card = 'Card',
  Wire = 'Wire',
  Check = 'Check',
}
export interface OfferOrderRefundDto {
  id: string;
  amount: number;
  mechanism: OfferOrderRefundMechanismDto;
  externalId?: string;
  status?: OfferOrderRefundStatusDto;
  reason?: OfferOrderRefundReasonDto;
  description?: string;
  failureReason?: OfferOrderRefundFailureReasonDto;
  bankName?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface OfferOrderConditionalDto {
  minSharePrice: number;
  maxSharePrice: number;
  totalInvestment: number;
}

export interface OfferOrderSecurityDeliveryOptionDto {
  label: string;
  tooltip?: string;
}

export interface OfferOrderExternalDetailsDto {
  securityDeliveryOption?: OfferOrderSecurityDeliveryOptionDto;
}

export interface CreateBaseOfferOrderDto {
  offerId: string;
  isAccreditedInvestor?: boolean;
  localeDate: string;
  securityDeliveryOption?: OfferOrderSecurityDeliveryOptionDto;
}

export interface CreateStandardOfferOrderBodyDto extends CreateBaseOfferOrderDto {
  quantity: number;
  paymentType?: OfferOrderPaymentTypeDto;
  cardPaymentIntentId?: string;
  retirementAccountFundingType?: OfferOrderRetirementAccountFundingTypeDto;
}

export interface CreateConditionalOfferOrderBodyDto extends CreateBaseOfferOrderDto {
  totalInvestment: number;
  paymentType?: Exclude<OfferOrderPaymentTypeDto, OfferOrderPaymentTypeDto.Card>;
  retirementAccountFundingType?: OfferOrderRetirementAccountFundingTypeDto;
}

export interface OfferOrderWireInstructionsDto extends OfferExternalWireInstructionsDto {
  transferAmount?: number;
}

export interface OfferOrderCheckInstructionsDto extends OfferExternalCheckInstructionsDto {
  transferAmount?: number;
}

export type OfferOrderPaymentInstructionsDto = OfferOrderWireInstructionsDto | OfferOrderCheckInstructionsDto;

export interface OfferOrderAnalyticsDto {
  isSubscriptionEventSent: boolean;
  isFundEventSent: boolean;
}

export interface OfferOrderDto {
  id: string;
  userId: string;
  offerId: string;
  offerName: string;
  offerCloseDate?: string;
  name?: string;
  symbol?: string;
  status: OfferOrderStatusDto;
  paymentType?: OfferOrderPaymentTypeDto;
  accountId: number;
  quantity: number;
  price: number;
  totalCost: number;
  isExternal: boolean;
  isAccreditedInvestor?: boolean;
  amountRefunded: number;
  refunds: OfferOrderRefundDto[];
  cardPayment?: OfferOrderCardPaymentDto;
  conditional?: OfferOrderConditionalDto;
  externalDetails?: OfferOrderExternalDetailsDto;
  retirementAccountFundingType?: OfferOrderRetirementAccountFundingTypeDto;
  localeDate: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface OfferOrderCardPaymentDetailsDto extends OfferOrderCardPaymentDto {}

export interface OfferOrderDetailsDto extends OfferOrderDto {
  paymentInstructions?: OfferOrderPaymentInstructionsDto;
  analytics: OfferOrderAnalyticsDto;
}

export interface CreateOfferOrderParams {
  accountId: number;
}

export type CreateOfferOrderBodyDto = CreateConditionalOfferOrderBodyDto | CreateStandardOfferOrderBodyDto;

export interface CreateOfferOrderDto {
  params: CreateOfferOrderParams;
  body: CreateOfferOrderBodyDto;
}

export interface GetOfferOrderParamsDto {
  id: string;
  accountId: number;
}

export interface GetOfferOrderDto {
  params: GetOfferOrderParamsDto;
}

export interface GetOfferOrderListQueryParamsDto
  extends PaginationQueryParams,
    SortDirectionQueryParams,
    DateRangeQueryParamsDto {
  status?: string;
}

export interface GetOfferOrderListParamsDto {
  accountId: number;
}
export interface GetOfferOrderListDto {
  params: GetOfferOrderListParamsDto;
  queryParams?: GetOfferOrderListQueryParamsDto;
}

export interface GetOfferOrderListResponseDto extends BaseListResponseDto<OfferOrderDto[]> {}

export interface UpdateOfferOrderBodyDto {
  analytics?: Partial<OfferOrderAnalyticsDto>;
}

export interface UpdateOfferOrderDto {
  params: GetOfferOrderParamsDto;
  body: UpdateOfferOrderBodyDto;
}

export interface CancelOfferOrderDto {
  params: GetOfferOrderParamsDto;
}

export interface FailOfferOrderDto {
  params: GetOfferOrderParamsDto;
}

export interface CompleteOfferOrderDto {
  params: GetOfferOrderParamsDto;
}

export interface CreateOfferOrderCardPaymentIntentParamsDto {
  accountId: number;
}

export interface CreateOfferOrderCardPaymentIntentBodyDto {
  quantity: number;
  offerId: string;
  accountId: number;
}

export interface CreateOfferOrderCardPaymentIntentDto {
  body: CreateOfferOrderCardPaymentIntentBodyDto;
}

export interface CreateOfferOrderCardPaymentIntentResponseDto {
  clientSecret: string;
  cardPaymentIntentId: string;
  totalCost: number;
}

export interface UpdateOfferOrderCardPaymentIntentParamsDto {
  id: string;
}

export interface UpdateOfferOrderCardPaymentIntentBodyDto {
  quantity: number;
  cardPaymentAccount: {
    externalId: string;
  };
}

export interface UpdateOfferOrderCardPaymentIntentDto {
  params: UpdateOfferOrderCardPaymentIntentParamsDto;
  body: UpdateOfferOrderCardPaymentIntentBodyDto;
}

export interface UpdateOfferOrderCardPaymentIntentResponseDto {
  clientSecret: string;
  cardPaymentIntentId: string;
  totalCost: number;
}
